<template>
  <div class="macos-dock">
    <div
      v-for="(item, index) in dockItems"
      :key="index"
      class="dock-item"
      :style="getItemStyle(index)"
      @click="handleClick(item)"
      @mouseenter="hoveredIndex = index"
      @mouseleave="hoveredIndex = -1"
    >
      <div class="dock-icon-wrapper">
        <!-- 使用多种图标方案确保显示 -->
        <component v-if="item.iconComponent" :is="item.iconComponent" class="dock-icon" />
        <Icon v-else-if="item.icon" :icon="item.icon" class="dock-icon" width="32" height="32" />
        <div v-else class="fallback-icon">
          {{ item.fallbackText || item.title.charAt(0) }}
        </div>
      </div>
      <span class="dock-label">{{ item.title }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';

// 也可以导入其他图标库
import { RectangleGroupIcon, MapIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline';
const router = useRouter();
const hoveredIndex = ref(-1);

// Dock 项目配置 - 使用多种图标方案确保显示
const dockItems = ref([
  {
    title: '首页',
    icon: 'mdi:home', // Iconify 图标
    iconComponent: RectangleGroupIcon,
    fallbackText: '🏠', // 备用文本图标
    path: '/',
    color: '#3b82f6',
    className: 'fas fa-home',
  },
  {
    title: '校点图',
    icon: 'mdi:home', // Iconify 图标
    iconComponent: MapIcon,
    fallbackText: '🏠', // 备用文本图标
    path: '/yuxi',
    color: '#3b82f6',
    className: 'fas fa-home',
  },
  {
    title: '撤并学校',
    icon: 'mdi:compass',
    iconComponent: XMarkIcon,
    fallbackText: '🔍',
    path: '/school-merge',
    color: '#10b981',
    className: 'far fa-map-marker-times',
  },
  {
    title: '增加学校',
    icon: 'mdi:message',
    iconComponent: PlusIcon,
    fallbackText: '💬',
    path: '/add-school',
    color: '#8b5cf6',
    badge: '3',
    className: 'far fa-map-marker-plus',
  },
]);

// 计算鼠标悬停时的缩放效果
const getItemStyle = (index) => {
  if (hoveredIndex.value === -1) return {};

  const distance = Math.abs(index - hoveredIndex.value);
  const scale = Math.max(1.8 - distance * 0.25, 1);
  const translateY = -Math.max(20 - distance * 4, 0);

  return {
    transform: `scale(${scale}) translateY(${translateY}px)`,
    zIndex: 100 - distance,
    transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  };
};

// 处理点击事件
const handleClick = (item) => {
  console.log(`点击了: ${item.title}`);

  if (item.path) {
    // 使用 Vue Router 进行导航
    router.push(item.path);
  }
};

// 在组件挂载后添加一些调试信息
onMounted(() => {
  console.log('macOS Dock 组件已加载');
  console.log('Dock 项目:', dockItems.value);
});
</script>

<style scoped>
.macos-dock {
  position: fixed;
  bottom: 30px;
  left: 60%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
}

.macos-dock:hover {
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.dock-item {
  position: relative;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  background: rgba(255, 255, 255, 0.05);
  margin-left: 10px;
}

.dock-item:hover {
  background: rgba(255, 255, 255, 0.15);
}

.dock-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.dock-icon {
  width: 32px;
  height: 32px;
  color: white;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.dock-item:hover .dock-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));
}

.fallback-icon {
  font-size: 24px;
  color: white;
  user-select: none;
}

.dock-label {
  position: absolute;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  z-index: 1001;
}

.dock-item:hover .dock-label {
  opacity: 1;
  transform: translateX(-50%) translateY(-5px);
}

/* 徽章样式 */
.dock-item::after {
  content: attr(data-badge);
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.3s ease;
}

.dock-item[data-badge]::after {
  opacity: 1;
  transform: scale(1);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .macos-dock {
    bottom: 15px;
    padding: 12px 20px;
    gap: 6px;
  }

  .dock-item {
    width: 52px;
    height: 52px;
  }

  .dock-icon {
    width: 28px;
    height: 28px;
  }

  .dock-label {
    font-size: 12px;
    padding: 5px 12px;
    bottom: -32px;
  }
}

@media (max-width: 480px) {
  .macos-dock {
    gap: 4px;
    padding: 10px 16px;
  }

  .dock-item {
    width: 48px;
    height: 48px;
  }

  .dock-icon {
    width: 24px;
    height: 24px;
  }

  /* 在小屏幕上隐藏部分文字较长的标签 */
  .dock-label {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 动画效果 */
@keyframes dockItemBounce {
  0%,
  100% {
    transform: scale(1) translateY(0);
  }

  50% {
    transform: scale(1.1) translateY(-5px);
  }
}

.dock-item.clicked {
  animation: dockItemBounce 0.5s ease;
}
</style>
